import { useState, useCallback } from 'react';
import * as socialApi from '../services/social-api';

export interface UseFollowReturn {
  isFollowing: boolean;
  isLoading: boolean;
  error: string | null;
  follow: () => Promise<void>;
  unfollow: () => Promise<void>;
  toggle: () => Promise<void>;
}

/**
 * Hook for managing follow/unfollow state for a target user.
 */
export function useFollow(userId: string, initialIsFollowing = false): UseFollowReturn {
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const follow = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await socialApi.followUser(userId);
      setIsFollowing(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to follow user');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const unfollow = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await socialApi.unfollowUser(userId);
      setIsFollowing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unfollow user');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const toggle = useCallback(async () => {
    if (isFollowing) {
      await unfollow();
    } else {
      await follow();
    }
  }, [isFollowing, follow, unfollow]);

  return { isFollowing, isLoading, error, follow, unfollow, toggle };
}
